import { useMemo } from 'react'
import { LocaleEnum } from '@systemeio/ui-frontend'
import DOMPurify from 'isomorphic-dompurify'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import useUser from 'shared/hooks/use-user'
import EllipseIcon from 'shared/icons/ellipse-icon'
import { timeSince } from 'shared/utils/date-time/time-since'
import { UserInfoPopover } from 'modules/community/components/user-info-popover/user-info-popover'
import { getReadableMemberWorkspaceRole } from 'modules/community/enums/member-enum'
import { PostInterface, SearchPostInterface } from 'modules/community/types/post-interface'

interface AuthorInfoProps {
  post: PostInterface | SearchPostInterface
  highlightedAuthorHTML?: string
}

export const PostAuthorInfo = ({ post, highlightedAuthorHTML }: AuthorInfoProps) => {
  const { t } = useTranslation()
  const { author, userId } = post
  const {
    query: { communityPath },
  } = useRouter()
  const { user } = useUser()

  const time = useMemo(() => {
    if (post.scheduledTo) {
      return new Date(post.scheduledTo).toLocaleDateString(
        user?.dashboardLocale ?? LocaleEnum.ENGLISH,
        {
          day: 'numeric',
          month: 'long',
          hour: '2-digit',
          minute: '2-digit',
        },
      )
    }
    return timeSince(new Date(post.publishedAt || post.createdAt), {
      locale: user?.dashboardLocale ?? LocaleEnum.ENGLISH,
    })
  }, [post.id, user?.dashboardLocale ?? LocaleEnum.ENGLISH])

  return (
    <p className="inline-flex h-fit items-start gap-2.5 text-text-secondary">
      <UserInfoPopover userId={userId} className="shrink-0">
        <img
          src={author?.avatarUrl || '/default_post_author_avatar.png'}
          alt={author?.displayName}
          className="size-10 rounded-full"
          width={40}
          height={40}
        />
      </UserInfoPopover>
      <div className="flex w-full flex-col *:h-5">
        <div className="flex items-center gap-1">
          <span
            className="max-h-[20px] max-w-[150px] truncate text-text-primary md:max-w-sm"
            dangerouslySetInnerHTML={{
              __html: highlightedAuthorHTML
                ? DOMPurify.sanitize(highlightedAuthorHTML)
                : author?.displayName || '',
            }}
          />
          <EllipseIcon />
          {author?.workspaceRole && (
            <span className="self-start rounded border border-transparent bg-brand-primary/[0.07] px-[5px] text-[10px] leading-5 text-brand-primary/[1]">
              {t(getReadableMemberWorkspaceRole(author.workspaceRole))}
            </span>
          )}
        </div>
        <div className="mt-auto flex w-full flex-row flex-nowrap gap-1">
          <span className="text-nowrap text-text-secondary">{time}</span>{' '}
          <span className="text-nowrap text-text-secondary">{t('community.pages_on')}</span>{' '}
          <Link
            className="inline-block max-w-[100px] truncate align-bottom font-semibold text-text-primary md:max-w-[200px]"
            href={`/community/${communityPath}/${post.topic.path}`}
            shallow
          >
            #{post.topic.name}
          </Link>
        </div>
      </div>
    </p>
  )
}
